import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, endpoints, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { toast } from "sonner";
import { Loader2, Building2, Megaphone, Disc3, Search } from "lucide-react";

const CONFIG = {
    venue: {
        label: "Venue",
        Icon: Building2,
        blurb: "Program events, manage door check-in, and grow a following.",
        workspaceRoute: (id) => `/venue/${id}/overview`,
        onboardEndpoint: "/onboarding/venue",
        searchEndpoint: "/onboarding/venues/search",
        extraFields: ["capacity", "address", "venue_type"],
        relationshipOptions: [
            { id: "owner", label: "I own this venue" },
            { id: "manager", label: "I manage this venue" },
            { id: "staff", label: "I work for / represent this venue" },
        ],
    },
    promoter: {
        label: "Promoter",
        Icon: Megaphone,
        blurb: "Book artists, program events across venues, and grow your audience.",
        workspaceRoute: (id) => `/promoter/${id}/overview`,
        onboardEndpoint: "/onboarding/promoter",
        searchEndpoint: "/onboarding/promoter/search",
        extraFields: ["markets"],
        // Natural-language wording per P0 feedback — no more "I own this
        // promoter". Map each choice to a stored relationship value so
        // the owner membership row remains consistent.
        relationshipOptions: [
            { id: "owner", label: "I am the promoter" },
            { id: "owner", label: "I own this promotion business", value: "owner_business" },
            { id: "admin", label: "I run it (executive / admin)" },
            { id: "team", label: "I work for / represent it" },
        ],
    },
    label: {
        label: "Record Label",
        Icon: Disc3,
        blurb: "Represent a roster, book shows, and coordinate with venues and promoters.",
        workspaceRoute: (id) => `/label/${id}/overview`,
        onboardEndpoint: "/onboarding/label",
        searchEndpoint: "/onboarding/label/search",
        extraFields: ["genres"],
        relationshipOptions: [
            { id: "owner", label: "Founder / Owner" },
            { id: "admin", label: "Executive / Admin" },
            { id: "team", label: "Team member" },
        ],
    },
};

export default function OrgSetup() {
    const { kind } = useParams();
    const nav = useNavigate();
    const { checkAuth } = useAuth();
    const cfg = CONFIG[kind];
    const [name, setName] = useState("");
    const [city, setCity] = useState("");
    const [country, setCountry] = useState("");
    const [contactEmail, setContactEmail] = useState("");
    const [website, setWebsite] = useState("");
    const [description, setDescription] = useState("");
    const [capacity, setCapacity] = useState("");
    const [address, setAddress] = useState("");
    const [venueType, setVenueType] = useState("");
    const [markets, setMarkets] = useState("");
    const [genres, setGenres] = useState("");
    const [relationship, setRelationship] = useState(cfg?.relationshipOptions[0]?.id || "owner");
    const [busy, setBusy] = useState(false);
    const [duplicates, setDuplicates] = useState([]);
    const [error, setError] = useState("");
    const [requesting, setRequesting] = useState("");
    const [searched, setSearched] = useState(false);

    const Icon = cfg?.Icon;
    const canSubmit = useMemo(() => name.trim().length > 1 && city.trim() && country.trim(), [name, city, country]);

    useEffect(() => {
        if (!cfg) nav("/", { replace: true });
    }, [cfg, nav]);

    const searchDuplicates = async () => {
        setSearched(true);
        if (name.trim().length < 2) {
            setError("Enter at least 2 characters of the " + (cfg?.label || "") + " name to search.");
            setDuplicates([]);
            return;
        }
        setError("");
        try {
            const r = await api.get(cfg.searchEndpoint, { params: { q: name } });
            setDuplicates(r.data?.results || []);
        } catch (err) {
            setError(formatApiError(err));
            setDuplicates([]);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError("");
        try {
            const payload = {
                name,
                city,
                country,
                contact_email: contactEmail || null,
                website: website || null,
                description: description || null,
                relationship,
            };
            if (kind === "venue") {
                if (capacity) payload.capacity = Number(capacity);
                if (address) payload.address = address;
                if (venueType) payload.venue_type = venueType;
            }
            if (kind === "promoter" && markets) payload.markets = markets.split(",").map((s) => s.trim()).filter(Boolean);
            if (kind === "label" && genres) payload.genres = genres.split(",").map((s) => s.trim()).filter(Boolean);
            const r = await api.post(cfg.onboardEndpoint, payload);
            const d = r.data || {};
            if (d.duplicate) {
                setDuplicates(d.candidates || []);
                setError("A matching " + cfg.label + " already exists. Request access below or refine your name/city.");
                setBusy(false);
                return;
            }
            const id = d.id || d[kind]?.id;
            await checkAuth();
            toast.success(`${cfg.label} created`);
            nav(cfg.workspaceRoute(id), { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const requestAccess = async (candidate) => {
        setRequesting(candidate.id);
        try {
            await api.post(`/${kind}s/${candidate.id}/access-requests`, { relationship, message: `Onboarding as ${relationship}` });
            toast.success("Access request sent");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRequesting("");
        }
    };

    if (!cfg) return null;

    return (
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-10" data-testid={`onboarding-${kind}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Project Mint</div>
            <h1 className="mt-1 font-display font-black uppercase tracking-tighter text-4xl">{cfg.label} setup</h1>
            <p className="mt-2 text-sm text-[#a3a3a3]">{cfg.blurb}</p>

            <form onSubmit={submit} className="mt-8 space-y-5">
                <Field label={`${cfg.label} name`} value={name} onChange={setName} onBlur={searchDuplicates} testId={`${kind}-name`} required />
                <div className="grid grid-cols-2 gap-3">
                    <Field label="City" value={city} onChange={setCity} testId={`${kind}-city`} required />
                    <Field label="Country" value={country} onChange={setCountry} testId={`${kind}-country`} required />
                </div>
                {kind === "venue" && (
                    <>
                        <Field label="Address (optional)" value={address} onChange={setAddress} testId="venue-address" />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Venue type (optional)" value={venueType} onChange={setVenueType} testId="venue-type" />
                            <Field label="Capacity (optional)" value={capacity} onChange={setCapacity} type="number" testId="venue-capacity" />
                        </div>
                    </>
                )}
                {kind === "promoter" && (
                    <Field label="Primary markets (comma-separated)" value={markets} onChange={setMarkets} testId="promoter-markets" />
                )}
                {kind === "label" && (
                    <Field label="Genres (comma-separated)" value={genres} onChange={setGenres} testId="label-genres" />
                )}
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Contact email (optional)" value={contactEmail} onChange={setContactEmail} type="email" testId={`${kind}-contact-email`} />
                    <Field label="Website (optional)" value={website} onChange={setWebsite} testId={`${kind}-website`} />
                </div>
                <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Your relationship</span>
                    <select
                        value={relationship}
                        onChange={(e) => setRelationship(e.target.value)}
                        className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none font-mono uppercase text-xs"
                        data-testid={`${kind}-relationship`}
                    >
                        {cfg.relationshipOptions.map((r) => (
                            <option key={r.id} value={r.id} className="bg-[#141414]">{r.label}</option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Short description (optional)</span>
                    <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full mt-1 bg-transparent border border-[#2a2a2a] focus:border-[#ff4400] py-2 px-2 outline-none text-sm" data-testid={`${kind}-description`} />
                </label>

                {duplicates.length > 0 ? (
                    <div className="border border-[#ff9500] p-4" data-testid={`${kind}-duplicates`}>
                        <div className="text-[10px] font-mono uppercase text-[#ff9500] mb-2">Possible matches on Project Mint</div>
                        <ul className="space-y-2">
                            {duplicates.map((c) => (
                                <li key={c.id} className="flex items-center gap-3 border border-[#2a2a2a] p-2">
                                    <Icon className="w-4 h-4 text-[#ff4400] shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold uppercase text-sm truncate">{c.name}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#666] truncate">{c.city || "—"}{c.country ? `, ${c.country}` : ""}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => requestAccess(c)}
                                        disabled={requesting === c.id}
                                        className="pm-btn text-[10px]"
                                        data-testid={`${kind}-request-access-${c.id}`}
                                    >
                                        {requesting === c.id ? "Sending…" : "Request access"}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (searched && !busy && name.trim().length >= 2 && (
                    <div className="border border-dashed border-[#2a2a2a] p-3 text-[10px] font-mono uppercase text-[#666]" data-testid={`${kind}-no-matches`}>
                        No existing {cfg.label.toLowerCase()} matches "{name.trim()}". You can create a new workspace below.
                    </div>
                ))}

                {error && <div className="text-xs font-mono uppercase text-[#ff3333]" data-testid={`${kind}-error`}>{error}</div>}

                <div className="flex items-center gap-3">
                    <button type="submit" disabled={busy || !canSubmit} className="pm-btn pm-btn-primary" data-testid={`${kind}-submit`}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Icon className="w-4 h-4 mr-2" />}
                        Create {cfg.label} workspace
                    </button>
                    <button type="button" onClick={searchDuplicates} className="pm-btn" data-testid={`${kind}-search-duplicates`}>
                        <Search className="w-4 h-4 mr-2" /> Search matches
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, value, onChange, onBlur, type = "text", testId, required }) {
    return (
        <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">{label}{required ? " *" : ""}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-[#f4f4f0]"
                data-testid={testId}
            />
        </label>
    );
}
